import { type PriceData } from "../../../../models/PriceData";
import { makeStyles } from "@mui/styles";
import { nonVolatileText, zero } from "../../../../utils/PriceUtils";
import { C_ON_SURFACE_VARIANT } from "../../icon/Colors";
import { Trans, useTranslation, type WithTranslation } from "react-i18next";
import { PopperTooltip } from "~/common/components/Tooltip";

export enum PriceType {
  Default,
  ProductNetPrice,
  ProductNetPriceFullLabel, // Special case, because DESIGN...
  ProductListPrice,
  ProductOtherPrice, // List Price, MSP, RSP
  ProductTotalNetPrice,
  ProductTotalOtherPrice,
  Subtotal,
  CartTotal,
}

export enum RebateLabelType {
  Default = "default",
  Full = "full",
}

interface BaseProps {
  classes?: CSSClasses;
  price: PriceData | undefined | null;
  includesSurcharge?: boolean;
  tariffSurcharge?: boolean;
  isSpecialPrice?: boolean;
  showSurchargeDisclaimer?: boolean;
  zeroText?: string;
  as?: keyof JSX.IntrinsicElements;
  hidden?: boolean;
}

interface DefaultProps extends BaseProps {
  variant: PriceType.Default;
  showZero?: boolean;
}

interface ProductNetPriceProps extends BaseProps {
  variant: PriceType.ProductNetPrice | PriceType.ProductNetPriceFullLabel;
  unit: number;
  isSubscription: boolean | undefined;
  rebateVariant?: RebateLabelType;
  rebateValue?: string | null;
}

export interface ProductOtherPriceProps extends BaseProps {
  variant: PriceType.ProductOtherPrice | PriceType.ProductListPrice;
  unit: number;
}

interface TotalPriceProps extends BaseProps {
  variant: PriceType.Subtotal | PriceType.CartTotal;
}

interface TotalProductPriceProps extends BaseProps {
  variant: PriceType.ProductTotalNetPrice | PriceType.ProductTotalOtherPrice;
  unit: number;
  isSubscription: boolean | undefined;
}

export type PriceProps =
  | DefaultProps
  | ProductNetPriceProps
  | ProductOtherPriceProps
  | TotalProductPriceProps
  | TotalPriceProps;

export const obtainPriceString = (props: PriceProps, t: WithTranslation["t"], zeroText: string | undefined) => {
  if (props.price && props.price.value > 0) {
    return nonVolatileText(props.price);
  }

  if (zeroText) {
    return t(zeroText);
  }

  // noinspection FallThroughInSwitchStatementJS
  switch (props.variant) {
    case PriceType.ProductNetPrice:
    case PriceType.ProductNetPriceFullLabel:
    case PriceType.CartTotal:
      return t("product.priceOnRequest");

    case PriceType.Default:
      if (props.showZero) {
        return nonVolatileText(zero());
      }

    default:
      return "-";
  }
};

export const obtainUnitLabel = (props: PriceProps, t: WithTranslation["t"]) => {
  const hasPrice = props.price && props.price.value > 0;
  if (!hasPrice && props.variant !== PriceType.ProductListPrice) {
    return null;
  }

  switch (props.variant) {
    case PriceType.CartTotal:
    case PriceType.Subtotal:
    case PriceType.Default:
      return null;

    case PriceType.ProductNetPrice: // per piece
      return t(props.isSubscription ? "common.product.perMonth" : "common.product.perPiece", { count: props.unit });

    case PriceType.ProductNetPriceFullLabel: // Net price per piece
      const netPrice = t("cartPage.table.header.price.title");
      const unitLabel = t(props.isSubscription ? "common.product.perMonth" : "common.product.perPiece", {
        count: props.unit,
      });
      return `${netPrice} ${unitLabel}`;

    case PriceType.ProductTotalNetPrice:
      return t(props.isSubscription ? "common.product.perYear" : "common.product.perPiece", { count: props.unit });

    case PriceType.ProductListPrice:
      return t("addProduct.salesman.listPricePerPiece", { count: props.unit });

    case PriceType.ProductOtherPrice:
    case PriceType.ProductTotalOtherPrice:
      return t("common.product.perPiece", { count: props.unit });
  }
};

export const obtainRebateLabel = (type: RebateLabelType, rebate: string, t: WithTranslation["t"]) => {
  if (type === RebateLabelType.Default) {
    // return small label
    return (
      <PopperTooltip
        tooltipContent={t("rebate.percentage.hover.label", { rebatePercentage: rebate })}
        placement="bottom-start"
      >
        <Trans
          i18nKey="rebate.percentage.small.label"
          values={{
            rebatePercentage: rebate,
          }}
          components={[<span />, <b style={{ marginLeft: 4 }} />]}
        />
      </PopperTooltip>
    );
  } else if (type === RebateLabelType.Full) {
    // return full label
    return (
      <Trans
        i18nKey="rebate.percentage.full.label"
        values={{
          rebatePercentage: rebate,
        }}
        components={[<span />, <b />, <span />]}
      />
    );
  }
};

export const obtainSpecialPriceLabel = (t: WithTranslation["t"]) => {
  return t("addProduct.salesman.priceTable.specialPrice");
};

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
  },
  price: {},
  label: {
    color: C_ON_SURFACE_VARIANT,
  },
  additionalLabel: {},
});

type CSSClasses = Partial<ReturnType<typeof useStyles>>;

export const Price = ({ hidden, ...props }: PriceProps) => {
  const { t } = useTranslation();
  const classes = useStyles(props);
  const Tag = props.as ?? "span";
  const { includesSurcharge, isSpecialPrice, tariffSurcharge } = props;

  const displayRebate =
    "rebateValue" in props &&
    props.rebateValue &&
    "rebateVariant" in props &&
    props.rebateVariant &&
    !props.isSubscription &&
    !includesSurcharge &&
    !isSpecialPrice;

  const priceString = obtainPriceString(props, t, props.zeroText);
  const unitLabel = obtainUnitLabel(props, t);
  const specialPriceLabel = obtainSpecialPriceLabel(t);
  // @ts-ignore
  const rebateLabel = displayRebate ? obtainRebateLabel(props.rebateVariant, props.rebateValue, t) : undefined;

  return (
    <Tag data-test-id={"price"} className={classes.root} hidden={hidden}>
      <span data-test-id={"value"} className={classes.price}>
        {priceString}
        {(includesSurcharge || tariffSurcharge) && "*"}
      </span>
      {unitLabel && (
        <span data-test-id={"unit"} className={classes.label}>
          {unitLabel}
        </span>
      )}
      {tariffSurcharge && (
        <span data-test-id={"unit"} className={"label-small"}>
          {t("price.tariff.surcharge.applicable")}
        </span>
      )}
      {isSpecialPrice && (
        <span data-test-id={"label"} className={classes.additionalLabel}>
          {specialPriceLabel}
        </span>
      )}
      {rebateLabel && (
        <span data-test-id={"label"} className={classes.additionalLabel}>
          {rebateLabel}
        </span>
      )}
    </Tag>
  );
};

interface MultiPriceProps {
  classes?: Pick<CSSClasses, "root" | "price">;
  as?: keyof JSX.IntrinsicElements;
}

export const MultiPrice = (props: MultiPriceProps) => {
  const classes = useStyles(props);

  const Tag = props.as ?? "span";

  const priceString = nonVolatileText(zero()).replace(/0/g, "-");

  return (
    <Tag data-test-id={"price"} className={classes.root}>
      <span data-test-id={"value"} className={classes.price}>
        {priceString}
      </span>
    </Tag>
  );
};
